import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { getMergedSx } from "@entrilia/shared/hooks/useMergedSx.ts";
import { Stack, useTheme } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridAggregationFunction,
  GridAlignment,
  GridApiPremium,
  GridColDef,
  GridColumnHeaderParams,
  GridColumnMenuProps,
  GridEventListener,
  GridGroupingColDefOverride,
  GridGroupingColDefOverrideParams,
  GridGroupNode,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import {
  DataGridPremium,
  getRowGroupingFieldFromGroupingCriteria,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  CalculationType,
  ColumnHorizontalAlignment,
} from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  GroupingField,
  SortConfiguration,
  TabularDataCell,
  TabularGeneralSettings,
  TabularRow,
  TabularSubtotalLabelPosition,
} from "../../../../../../../api/types/svTypes.ts";
import useAutosizeColumns from "../../../hooks/useAutosizeColumns.ts";
import { getBiDataGridStyles } from "../../../utilities/Utilities.tsx";
import ReportLoadingOverlay from "../../common/ReportLoadingOverlay.tsx";
import ColumnWidthPopover from "./column-menu/ColumnWidthPopover.tsx";
import CustomColumnMenu from "./column-menu/CustomColumnMenu.tsx";
import ColumnResizeTooltip from "./ColumnResizeTooltip.tsx";
import GroupLevelExpansionButton from "./GroupLevelExpansionButton.tsx";
import { getAggregationModel } from "./utils/getAggregationModel.ts";
import { getAggregationPosition } from "./utils/getAggregationPosition.ts";
import { getSortingModel } from "./utils/getSortingModel.ts";
import { fromGridAlignment } from "./utils/tabularColumnAlignment.ts";
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from "./utils/tabularColumnWidths.ts";
import { handleRenderCell, renderGroupingColumnHeader } from "./utils/tabularGridDataProvider.tsx";

interface Props extends Pick<
  DataGridPremiumProps,
  | "rowGroupingColumnMode"
  | "aggregationModel"
  | "aggregationFunctions"
  | "pinnedColumns"
  | "showColumnVerticalBorder"
  | "sx"
  | "groupingColDef"
  | "autosizeOptions"
  | "rowGroupingModel"
  | "apiRef"
  | "initialState"
  | "sortingMode"
  | "dataSource"
  | "dataSourceCache"
  | "onDataSourceError"
  | "rows"
  | "loading"
  | "onColumnOrderChange"
  | "onRowGroupingModelChange"
  | "onSortModelChange"
  | "onCellClick"
  | "onPinnedColumnsChange"
> {
  maxWidth?: number;
  columnWidths: ReadonlyMap<string, number>;
  customColumnWidths: ReadonlyMap<string, number>;
  columnAlignments: ReadonlyMap<string, GridAlignment>;
  onColumnWidthChange: (field: string, width: number | undefined, manual: boolean, applyToAll?: boolean) => void;
  onColumnAlignmentChange?: (field: string, alignment: ColumnHorizontalAlignment | undefined) => void;
  showGroupedColumns: boolean;
  subtotalLabelPosition: TabularSubtotalLabelPosition;
  subtotalLabelFormatPrefix: string;
  subtotalLabelFormatSuffix: string;
  freezedColumnIds: string[];
  groupingFields: GroupingField[];
  apiRef: RefObject<GridApiPremium | null>;
  sortingFields: SortConfiguration[];
  fieldsIds: string[];
  groupSummaryPlace: TabularGeneralSettings["groupSummaryPlace"];
  showGrandTotal: boolean;
  disableColumnManagement: boolean;
  columns: GridColDef[];
  onPredefinedCustomFieldType?: (
    calculationType: CalculationType,
    fieldGuid: string,
    anchorEl: HTMLElement | null
  ) => void;
  isColumnFailedToLoad: (fieldGuid: string) => boolean;
}

const custom: GridAggregationFunction<number, number> = {
  label: "",
  apply: () => 0,
  columnTypes: ["string"],
};
const aggregationFunctions: Record<string, GridAggregationFunction<number, number>> = {
  custom: custom,
};

const emptyOverlay = () => null;
const emptyRows: TabularRow[] = [];

const tabularGridSx = {
  "[data-id^=auto-generated-group-footer][role=row]": {
    bgcolor: "background.grey",
  },
};

export function TabularGridCore({
  maxWidth,
  showGroupedColumns,
  freezedColumnIds,
  subtotalLabelPosition,
  subtotalLabelFormatPrefix,
  subtotalLabelFormatSuffix,
  groupingFields,
  apiRef,
  sortingFields,
  fieldsIds,
  showGrandTotal,
  groupSummaryPlace,
  disableColumnManagement,
  columns,
  columnWidths,
  customColumnWidths,
  columnAlignments,
  onColumnWidthChange,
  onColumnAlignmentChange,
  rows,
  dataSource,
  dataSourceCache,
  onDataSourceError,
  loading,
  onPredefinedCustomFieldType,
  onSortModelChange,
  onColumnOrderChange,
  onRowGroupingModelChange,
  onCellClick,
  onPinnedColumnsChange,
  isColumnFailedToLoad,
}: Props) {
  const theme = useTheme();
  const [columnWidthEditor, setColumnWidthEditor] = useState<{
    column: GridColDef;
    width: number;
    anchorEl: HTMLElement;
  }>();
  const manuallyResizedColumnsRef = useRef(new Set(customColumnWidths.keys()));
  useLayoutEffect(() => {
    manuallyResizedColumnsRef.current = new Set(customColumnWidths.keys());
  }, [customColumnWidths]);
  const handleColumnResize = useCallback<GridEventListener<"columnResize">>(({ colDef }) => {
    manuallyResizedColumnsRef.current.add(colDef.field);
  }, []);
  const handleColumnWidthChange = useCallback<GridEventListener<"columnWidthChange">>(
    ({ colDef, width }) => {
      onColumnWidthChange(colDef.field, width, manuallyResizedColumnsRef.current.has(colDef.field));
    },
    [onColumnWidthChange]
  );
  const handleSetWidth = useCallback(
    (column: GridColDef) => {
      const api = apiRef.current;
      const current = api?.getColumn(column.field);
      const anchorEl = api?.getColumnHeaderElement(column.field);
      if (api && current && anchorEl) {
        setColumnWidthEditor({
          column,
          width: current.computedWidth,
          anchorEl,
        });
      }
    },
    [apiRef]
  );
  const handleSetAlignment = useCallback(
    (column: GridColDef, align: GridAlignment | undefined) => {
      if (!disableColumnManagement) {
        onColumnAlignmentChange?.(column.field, align ? fromGridAlignment(align) : undefined);
      }
    },
    [onColumnAlignmentChange, disableColumnManagement]
  );
  const handleApplyWidth = (width: number | undefined, applyToAll: boolean) => {
    if (columnWidthEditor && !disableColumnManagement) {
      if (applyToAll) {
        onColumnWidthChange(columnWidthEditor.column.field, width, true, true);
      } else if (width === undefined) {
        manuallyResizedColumnsRef.current.delete(columnWidthEditor.column.field);
        onColumnWidthChange(columnWidthEditor.column.field, undefined, true);
      } else {
        manuallyResizedColumnsRef.current.add(columnWidthEditor.column.field);
        if (apiRef.current?.getColumn(columnWidthEditor.column.field)?.computedWidth === width) {
          onColumnWidthChange(columnWidthEditor.column.field, width, true);
        } else {
          apiRef.current?.setColumnWidth(columnWidthEditor.column.field, width);
        }
      }
    }
    setColumnWidthEditor(undefined);
  };
  const suppressGridLoader = !!dataSource && loading;
  const slots = useMemo(
    () => ({
      loadingOverlay: suppressGridLoader ? emptyOverlay : ReportLoadingOverlay,
      ...(suppressGridLoader ? { noRowsOverlay: emptyOverlay } : {}),
      columnMenu: (props: GridColumnMenuProps) => (
        <CustomColumnMenu
          {...props}
          onPredefinedCustomFieldType={onPredefinedCustomFieldType}
          onSetWidth={handleSetWidth}
          onSetAlignment={handleSetAlignment}
        />
      ),
    }),
    [suppressGridLoader, onPredefinedCustomFieldType, handleSetWidth, handleSetAlignment]
  );
  const gridColumns = useMemo(
    () =>
      columns.map((column) => {
        const align = columnAlignments.get(column.field);
        return {
          ...column,
          width: columnWidths.get(column.field) ?? column.width,
          ...(align ? { align, headerAlign: align } : {}),
        };
      }),
    [columns, columnWidths, columnAlignments]
  );
  const rowGroupingColumnMode = showGroupedColumns ? "multiple" : "single";
  const groupingModel = useMemo(() => groupingFields.map((gf) => gf.id), [groupingFields]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: dataSource ? { pagination: { paginationModel: { page: 0, pageSize: 500 } } } : {},
    rowGroupingModel: groupingModel,
  });
  const sortModel = useMemo(() => getSortingModel(sortingFields, fieldsIds), [sortingFields, fieldsIds]);
  const aggregationModel = useMemo(() => getAggregationModel(columns), [columns]);
  const calculateAggregationModel = useCallback(
    (groupNode: GridGroupNode) => {
      return getAggregationPosition(groupNode, showGrandTotal, groupSummaryPlace, groupingFields);
    },
    [groupingFields, groupSummaryPlace, showGrandTotal]
  );

  const freezedColumnsModel = useMemo(() => {
    return { left: freezedColumnIds, right: [] };
  }, [freezedColumnIds]);

  const getGroupColumnDef = useCallback(
    (params: GridGroupingColDefOverrideParams): GridGroupingColDefOverride => {
      const leafField = showGroupedColumns ? params.fields[0] : undefined;
      const headerName = leafField ? apiRef?.current?.getColumn(leafField)?.headerName : " ";
      const groupingField = getRowGroupingFieldFromGroupingCriteria(leafField ?? null);
      const align = columnAlignments.get(groupingField);

      return {
        hideDescendantCount: true,
        headerName: headerName,
        sortable: false,
        aggregable: false,
        hideable: false,
        pinnable: false,
        filterable: false,
        minWidth: MIN_COLUMN_WIDTH,
        maxWidth: MAX_COLUMN_WIDTH,
        width: columnWidths.get(groupingField),
        ...(align ? { align, headerAlign: align } : {}),
        leafField: leafField,
        renderCell: (params: GridRenderCellParams<TabularRow, TabularDataCell>) => {
          return handleRenderCell(
            params,
            rowGroupingColumnMode,
            subtotalLabelPosition,
            subtotalLabelFormatPrefix,
            subtotalLabelFormatSuffix
          );
        },
        renderHeader: ({ colDef }: GridColumnHeaderParams<TabularRow, TabularDataCell>) => {
          return (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              <GroupLevelExpansionButton field={leafField} caption={headerName ?? leafField} />
              {renderGroupingColumnHeader(colDef, rowGroupingColumnMode, params.fields, apiRef, isColumnFailedToLoad)}
            </Stack>
          );
        },
      };
    },
    [
      columnWidths,
      columnAlignments,
      rowGroupingColumnMode,
      showGroupedColumns,
      apiRef,
      subtotalLabelPosition,
      subtotalLabelFormatPrefix,
      subtotalLabelFormatSuffix,
      isColumnFailedToLoad,
    ]
  );

  const sx = useMemo(
    () => getMergedSx(theme, getBiDataGridStyles, { maxWidth: maxWidth }, tabularGridSx),
    [theme, maxWidth]
  );

  const { autosizeOptions } = useAutosizeColumns(
    apiRef,
    [
      columns,
      customColumnWidths,
      showGroupedColumns,
      subtotalLabelPosition,
      subtotalLabelFormatPrefix,
      subtotalLabelFormatSuffix,
    ],
    manuallyResizedColumnsRef
  );

  const handleSortModelChange = useCallback<NonNullable<DataGridPremiumProps["onSortModelChange"]>>(
    (sortModel, details) => {
      if (sortModel.length === 0 && apiRef?.current?.getSortModel()?.length !== 0) {
        return;
      }
      onSortModelChange?.(sortModel, details);
    },
    [onSortModelChange, apiRef]
  );

  return (
    <ScrollableFlexContainer scrollContainerSx={{ display: "flex", justifyContent: "center" }}>
      <DataGridPremium<TabularRow>
        apiRef={apiRef}
        sx={sx}
        showColumnVerticalBorder
        rowHeight={32}
        columnHeaderHeight={32}
        disableRowSelectionOnClick
        disableColumnSelector
        hideFooter
        groupingColDef={getGroupColumnDef}
        sortingOrder={["asc", "desc"]}
        sortingMode={dataSource ? "server" : "client"}
        dataSource={dataSource}
        dataSourceCache={dataSourceCache}
        onDataSourceError={onDataSourceError}
        lazyLoading={!!dataSource}
        defaultGroupingExpansionDepth={-1}
        disablePivoting
        slots={slots}
        rowGroupingColumnMode={rowGroupingColumnMode}
        aggregationModel={aggregationModel}
        aggregationFunctions={aggregationFunctions}
        pinnedColumns={freezedColumnsModel}
        autosizeOptions={autosizeOptions}
        rowGroupingModel={groupingModel}
        initialState={initialState}
        sortModel={sortModel}
        getAggregationPosition={calculateAggregationModel}
        disableColumnMenu={disableColumnManagement}
        disableColumnResize={disableColumnManagement}
        disableColumnReorder={disableColumnManagement}
        onSortModelChange={handleSortModelChange}
        columns={gridColumns}
        onColumnWidthChange={handleColumnWidthChange}
        onColumnResize={handleColumnResize}
        rows={dataSource ? emptyRows : rows}
        loading={dataSource ? false : loading}
        onColumnOrderChange={onColumnOrderChange}
        onRowGroupingModelChange={onRowGroupingModelChange}
        onCellClick={onCellClick}
        onPinnedColumnsChange={onPinnedColumnsChange}
      />
      <ColumnResizeTooltip apiRef={apiRef} />
      {columnWidthEditor && (
        <ColumnWidthPopover
          width={columnWidthEditor.width}
          autosize={!customColumnWidths.has(columnWidthEditor.column.field)}
          anchorEl={columnWidthEditor.anchorEl}
          onClose={() => setColumnWidthEditor(undefined)}
          onApply={handleApplyWidth}
        />
      )}
      {dataSource && loading && <ReportLoadingOverlay />}
    </ScrollableFlexContainer>
  );
}
